import type { CorpusEntry } from '@rp/contracts'
import { useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Button, Chip, Dialog, EmptyState, LedgerRow } from '../../design'
import { formatDateTime } from '../../lib/time'
import { CORPUS_ADMISSION_LABELS, CORPUS_COPY, previewOf, retireReasonLabel } from './copy'

const EntryMeta = ({ entry }: { entry: CorpusEntry }) => (
  <p className="corpus-entry-meta">
    <Chip variant="outline">{CORPUS_ADMISSION_LABELS[entry.admittedVia]}</Chip>
    <span>{CORPUS_COPY.admittedBy(entry.admittedByName)}</span>
    <span className="num">{formatDateTime(new Date(entry.admittedAt))}</span>
    {entry.retiredAt === null ? null : (
      <>
        <Chip tone="warning">{retireReasonLabel(entry.retiredReason)}</Chip>
        <span>{CORPUS_COPY.retiredOn(formatDateTime(new Date(entry.retiredAt)))}</span>
      </>
    )}
  </p>
)

const EntryRow = ({
  entry,
  retiring,
  onOpen,
  onConfirm,
}: {
  entry: CorpusEntry
  retiring: boolean
  onOpen: (conversationId: string) => void
  onConfirm: (entry: CorpusEntry) => void
}) => (
  <LedgerRow state={entry.status === 'retired' ? 'muted' : 'default'}>
    <div className="corpus-entry">
      {entry.inboundContext === null ? null : (
        <p className="corpus-entry-question">
          <span className="corpus-entry-label">{CORPUS_COPY.question}</span>
          <span>{entry.inboundContext}</span>
        </p>
      )}
      <p className="corpus-entry-text">{entry.replyText}</p>
      <EntryMeta entry={entry} />
      <div className="corpus-entry-actions">
        {entry.conversationId === null ? null : (
          <Button onClick={() => onOpen(entry.conversationId ?? '')}>{CORPUS_COPY.openConversation}</Button>
        )}
        {entry.status === 'retired' ? null : (
          <Button
            variant="danger"
            className="corpus-retire"
            pending={retiring}
            pendingLabel={CORPUS_COPY.retiring}
            onClick={() => onConfirm(entry)}
          >
            {CORPUS_COPY.retire}
          </Button>
        )}
      </div>
    </div>
  </LedgerRow>
)

export const CorpusEntryList = ({
  entries,
  emptyTitle,
  retiringId,
  onRetire,
}: {
  entries: readonly CorpusEntry[]
  emptyTitle: string
  retiringId: string | null
  onRetire: (id: string) => void
}) => {
  const navigate = useNavigate()
  const [confirming, setConfirming] = useState<CorpusEntry | null>(null)

  if (entries.length === 0) return <EmptyState title={emptyTitle} description={CORPUS_COPY.emptyBody} />

  return (
    <>
      <ul className="ledger-list corpus-list" aria-label={CORPUS_COPY.entries}>
        {entries.map((entry) => (
          <EntryRow
            key={entry.id}
            entry={entry}
            retiring={retiringId === entry.id}
            onOpen={(itemId) => void navigate({ to: '/items/$itemId', params: { itemId } })}
            onConfirm={setConfirming}
          />
        ))}
      </ul>
      <Dialog
        open={confirming !== null}
        title={CORPUS_COPY.confirmRetireTitle}
        onClose={() => setConfirming(null)}
        footer={
          <>
            <Button onClick={() => setConfirming(null)}>{CORPUS_COPY.confirmRetireCancel}</Button>
            <Button
              variant="danger"
              className="corpus-retire-confirm"
              onClick={() => {
                if (confirming === null) return
                onRetire(confirming.id)
                setConfirming(null)
              }}
            >
              {CORPUS_COPY.confirmRetireConfirm}
            </Button>
          </>
        }
      >
        <p className="corpus-confirm-body">
          {confirming === null ? '' : CORPUS_COPY.confirmRetireBody(previewOf(confirming.replyText))}
        </p>
      </Dialog>
    </>
  )
}
